<template>
  <Transition
    enter-active-class="transition ease-out duration-500"
    enter-from-class="-translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition ease-in duration-300"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="-translate-y-full opacity-0"
  >
    <div
      v-if="shouldShowAlert"
      class="w-full floating-notice mb-6"
    >
      <div
        class="bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 border-2 border-orange-400 rounded-xl p-4 backdrop-blur-sm bg-opacity-95 shadow-lg relative"
      >
        <!-- Dismiss Button -->
        <button
          @click="dismissAlert"
          class="absolute top-3 right-3 text-orange-700 hover:text-orange-900 hover:bg-orange-200 rounded-full p-1 transition-all duration-200"
          title="Dismiss alert"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div class="flex items-start space-x-3 pr-8">
          <div class="flex-shrink-0 mt-0.5">
            <div
              class="w-8 h-8 bg-orange-400 rounded-full flex items-center justify-center animate-pulse"
            >
              <svg class="w-5 h-5 text-orange-900" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <div class="text-sm text-orange-900 break-words flex-1">
            <strong class="font-bold text-base">Action Required: Document Resubmission Needed</strong>
                  <p class="mt-1">
                    Please resubmit the required documents to complete your application verification.
                  </p>
            <div class="mt-3">
              <router-link
                to="/student/application-status"
                class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Resubmit Documents
                <svg class="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useStudentStatus } from '@/composables/useStudentStatus';

// Use the student status composable
const { status: studentStatus } = useStudentStatus();

// Local state for dismissal
const isDismissed = ref(false);

// Show alert only when student has document_issue status and hasn't dismissed it
const shouldShowAlert = computed(() => {
  return studentStatus.value === 'document_issue' && !isDismissed.value;
});

// Dismiss the alert
const dismissAlert = () => {
  isDismissed.value = true;
  // Don't store dismissal in sessionStorage - show on every login
};

// Reset dismissal state when student status changes to document_issue
watch(() => studentStatus.value, (newStatus) => {
  if (newStatus === 'document_issue') {
    isDismissed.value = false;
  }
});
</script>

<style scoped>
/* Subtle floating animation for Document Resubmission Alert */
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

.floating-notice {
  animation: float 3s ease-in-out infinite;
}

/* Hover effect - pause animation and slightly scale up */
.floating-notice:hover {
  animation-play-state: paused;
  transform: scale(1.02) translateY(0px) !important;
  transition: transform 0.2s ease;
}
</style>
